import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { RulesValidationSchema } from "@/shared";
import { usePermission, useToast, useUpdateRuleMutation } from "@/hooks";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Rule, UpdateRuleDTO } from "@/types";
import { DisplayError } from "@/utils";
import { AlertDialogDelete } from "../../dialogs";

type FormUpdateRule = {
  handleClose: () => void;
  rule: Rule;
};

export function FormUpdateRule({ handleClose, rule }: FormUpdateRule) {
  const { t } = useTranslation();

  const { checkPermission } = usePermission();

  const { toast } = useToast();

  const initialValues = {
    id: rule.id,
    name: t(rule.name as string) || "",
  };

  const { mutateAsync, isLoading } = useUpdateRuleMutation();

  const { handleSubmit, ...formik } = useFormik<UpdateRuleDTO>({
    initialValues,
    validationSchema: RulesValidationSchema(),
    onSubmit: async (rolesData) => {
      await handleCreateRoleSubmit(rolesData);
    },
  });

  async function handleCreateRoleSubmit(request: UpdateRuleDTO) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
          formik.resetForm();
          handleClose();
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10 px-4 pb-8">
      <div className="flex flex-col gap-2">
        <Label>{t("LABEL.name")}</Label>
        <Input
          isInvalid={formik.touched.name && formik.errors.name}
          isValid={formik.touched.name && !formik.errors.name}
          {...formik.getFieldProps("name")}
          messageError={formik.errors.name}
        />
        <div className="w-full space-y-2">
          <Button isLoading={isLoading} className="w-full" disabled={!checkPermission("role", "update role") || isLoading}>
            {t("LABEL.save")}
          </Button>
          <AlertDialogDelete
            endpoint={`/roles/${rule.id}`}
            queryKey="roles"
            callBack={handleClose}
          >
            <Button
              type="button"
              className="w-full bg-destructive hover:bg-destructive/80"
              disabled={!checkPermission("role", "delete role")}
            >
              {t("LABEL.delete")}
            </Button>
          </AlertDialogDelete>
        </div>
      </div>
    </form>
  );
}
